/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import React from "react";
import * as Icons from "./Icons";
import { Icon, SvgIconProps } from "@mui/material";

type MUIIconProps = {
  iconName: keyof typeof Icons | string;
  size?: SvgIconProps["fontSize"];
  color?: SvgIconProps["color"];
  sx?: object;
  onClick?: () => void;
};

const MUIIcons: React.FC<MUIIconProps> = ({
  iconName,
  size = "small",
  color = "inherit",
  sx = {},
  onClick,
}) => {
  const IconComponent = Icons[iconName as keyof typeof Icons];

  const isFromIcons = !!IconComponent;
  const isStringIcon = typeof iconName === "string";

  if (!isFromIcons && isStringIcon) {
    if (!/^[a-z_]+$/i.test(iconName)) {
      console.warn(`Icon "${iconName}" not found in GlobalIcons.`);
    }

    return (
      <Icon
        fontSize={size}
        color={color}
        sx={{ cursor: onClick ? "pointer" : undefined, ...sx }}
        onClick={onClick}
        data-testid="font-icon"
      >
        {iconName}
      </Icon>
    );
  }

  if (isFromIcons) {
    const Component = IconComponent as React.ElementType;
    return (
      <Component
        fontSize={size}
        color={color}
        sx={{ cursor: onClick ? "pointer" : undefined, ...sx }}
        onClick={onClick}
        data-testid="svg-icon"
      />
    );
  }

  return null;
};

export default MUIIcons;
